import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Speed } from '@mui/icons-material';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../../dashboard/chartTokens';
import { formatINR, formatNumber, formatPct } from '../../../dashboard/primitives';
import { SalesPipeline } from '../../../types/salesAnalytics';
import { PIPELINE_STAGE_LABELS } from '../../../pages/sales/salesReportsHelpers';

interface PipelineSectionProps {
  pipeline: SalesPipeline | null;
  loading: boolean;
}

/**
 * Pipeline composition (2 series, same measure — currency — so one shared
 * axis, never dual-axis) plus velocity with its formula spelled out so the
 * headline figure is auditable rather than a black box.
 */
const PipelineSection: React.FC<PipelineSectionProps> = ({ pipeline, loading }) => {
  const t = useChartTokens();

  if (loading || !pipeline) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, mb: 3, minHeight: 320 }} />
    );
  }

  const chartData = pipeline.byStage.map((s) => ({
    stage: PIPELINE_STAGE_LABELS[s.stage] ?? s.stage,
    'Total value': s.totalValue,
    'Weighted value': s.weightedValue,
    count: s.count,
  }));

  const { velocity } = pipeline;
  const { inputs } = velocity;
  const missing: string[] = [];
  if (inputs.avgDealSize === null) missing.push('average deal size (no deals won yet)');
  if (inputs.winRatePct === null) missing.push('win rate (no deals closed yet)');
  if (inputs.avgSalesCycleDays === null || inputs.avgSalesCycleDays <= 0)
    missing.push('sales cycle length (no deals won yet, or cycle is 0 days)');

  return (
    <Grid container spacing={2.5} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, md: 8 }}>
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, height: '100%' }}>
          <Typography sx={{ fontWeight: 700, mb: 0.25 }}>Pipeline by Stage</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Live snapshot of open and closed opportunities — not filtered to the selected period.
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid {...gridProps(t)} />
                <XAxis dataKey="stage" {...axisProps(t)} />
                <YAxis {...axisProps(t)} width={64} tickFormatter={(v: number) => formatINR(v, true)} />
                <Tooltip
                  {...tooltipStyle(t)}
                  formatter={(v: any, name: any) => [formatINR(Number(v)), name]}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: t.textSecondary }} />
                <Bar dataKey="Total value" fill={t.series(0)} radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Bar dataKey="Weighted value" fill={t.series(1)} radius={[4, 4, 0, 0]} maxBarSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: '#6366f1', color: '#fff' }}>
              <Speed sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700 }}>Pipeline Velocity</Typography>
          </Box>

          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#6366f1', fontVariantNumeric: 'tabular-nums', mb: 0.5 }}>
            {velocity.value === null ? '—' : `${formatINR(velocity.value, true)} / day`}
          </Typography>

          {velocity.value === null ? (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1.5 }}>
              Not computable — missing {missing.join('; ')}.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Expected pipeline throughput per day at current win rate and cycle length.
            </Typography>
          )}

          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
              (open deals × avg deal size × win rate) ÷ cycle days
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Row label="Open deals" value={formatNumber(inputs.openCount)} />
              <Row label="Avg deal size" value={inputs.avgDealSize === null ? '—' : formatINR(inputs.avgDealSize, true)} />
              <Row label="Win rate" value={inputs.winRatePct === null ? '—' : formatPct(inputs.winRatePct)} />
              <Row label="Cycle length" value={inputs.avgSalesCycleDays === null ? '—' : `${formatNumber(inputs.avgSalesCycleDays)} d`} />
            </Box>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Box>
);

export default PipelineSection;
